import React, { useState } from "react";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { isEmpty } from "lodash";
import { oc } from "ts-optchain";
import styled from "@emotion/styled";
import { Location, LocationContext } from "@reach/router";
import { FadeIn } from "@utils";
import { Box, Text, Row, Column, TouchableOpacity, H3 } from "@atoms";
import { useLoadingDelay } from "@hooks";
import { BrandModalHero } from "../BrandModalHero";
import { BrandModalLinks } from "../BrandModalLinks";
import { BrandModalLoading } from "../BrandModalLoading";
import { BrandModalAbout } from "../BrandModalAbout";
import { BrandModalQuery, BrandModalQueryVariables } from "./__generated__/BrandModalQuery";

// == Types ================================================================

interface IProps {
  brandId: string;
  isLoading?: boolean;
  locationId?: string;
}

// == Constants ============================================================

const TABS = {
  OFFER: "OFFER",
  ABOUT: "ABOUT",
};

const BRAND_LOCATION_FRAGMENT = {
  fields: gql`
    fragment BrandLocationFragment on Location {
      id
      street1
      street2
      province
      city
      postalCode
      phoneNumber
      latitude
      longitude
      googlePlacesId
      hasLinkedCard
      metaInfo {
        rating
        ratingsCount
        priceRange
        primaryCategory
        categories
        ambiance
        menuUrl
        mealTypesServed
        attire
        knownFor
        bestNightsToVisit
      }
    }
  `,
};

const BRAND_MODAL_QUERY = gql`
  query BrandModalQuery($id: ID!, $locationId: ID) {
    Brand(id: $id) {
      id
      name
      description
      ...BrandModalHeroFragment
      ...BrandModalLinksFragment
      location(id: $locationId) {
        id
        ...BrandLocationFragment
      }
      nearestLocation {
        id
        ...BrandLocationFragment
      }
    }
  }
  ${BrandModalHero.fragments.fields}
  ${BrandModalLinks.fragments.fields}
  ${BRAND_LOCATION_FRAGMENT.fields}
`;

// == Component ============================================================

const BrandModal = ({ brandId, locationId, isLoading }: IProps) => {
  const [activeTab, setActiveTab] = useState(TABS.OFFER);
  const loadingDelayReady = useLoadingDelay();

  const { data, error, loading } = useQuery<BrandModalQuery, BrandModalQueryVariables>(
    BRAND_MODAL_QUERY,
    {
      variables: {
        id: brandId,
        locationId: locationId,
      },
    }
  );

  if (error) {
    return (
      <Box>
        <Text>{error.message}</Text>
      </Box>
    );
  }
  const hasNotLoaded = isEmpty(data) || loading || !loadingDelayReady || isLoading;

  if (hasNotLoaded) return <BrandModalLoading />;

  const brand = oc(data).Brand();
  // possible because the brand queried may not be returned
  if (!brand) {
    return (
      <Box>
        <Text>Sorry couldn&apos;t find that brand</Text>
      </Box>
    );
  }

  const renderTab = ({
    label,
    active,
    onTouch,
  }: {
    label: string;
    active: boolean;
    onTouch: () => void;
  }) => {
    return (
      <TouchableOpacity containerProps={{ style: { width: "100%" } }} onTouch={onTouch}>
        <Box borderBottom={active ? "primary" : null} borderWidth={4}>
          <Text
            color={active ? "primary" : "default"}
            display="block"
            px={1}
            py={3}
            textAlign="center"
          >
            {label}
          </Text>
        </Box>
      </TouchableOpacity>
    );
  };

  const renderTabs = () => {
    return (
      <ETabsContainer borderBottom="silver">
        <Row>
          <Column autoColumn>
            {renderTab({
              label: "Offer",
              active: activeTab === TABS.OFFER,
              onTouch: () => setActiveTab(TABS.OFFER),
            })}
          </Column>
          <Column autoColumn>
            {renderTab({
              label: "About",
              active: activeTab === TABS.ABOUT,
              onTouch: () => setActiveTab(TABS.ABOUT),
            })}
          </Column>
        </Row>
      </ETabsContainer>
    );
  };

  const renderBrandModal = ({ location, navigate }: LocationContext) => {
    const onBackClick = () => {
      if (location.state.canReturn) {
        window.history.back();
      } else {
        navigate(location.pathname);
      }
    };

    const { tinyLinkKey } = location.state;
    const brandLocation = locationId ? brand.location : brand.nearestLocation;
    return (
      <FadeIn>
        <EModalContainer>
          <BrandModalHero location={brandLocation} brand={brand} onBackClick={onBackClick} />

          {renderTabs()}

          {activeTab === TABS.OFFER && (
            <Box p={3}>
              <BrandModalLinks brand={brand} location={brandLocation} tinyLinkKey={tinyLinkKey} />
            </Box>
          )}

          {activeTab === TABS.ABOUT && (
            <Box>
              <BrandModalAbout brand={brand} location={brandLocation} />
            </Box>
          )}
        </EModalContainer>
      </FadeIn>
    );
  };

  return (
    <Location>
      {({ location, navigate }: LocationContext) => renderBrandModal({ location, navigate })}
    </Location>
  );
};
export default BrandModal;

// == Styles ===============================================================

const ETabsContainer = styled(Box)`
  overflow: hidden;
`;

const EModalContainer = styled(Box)`
  min-height: 700px;
`;
